<template>
  <div>
    <div class="tw-flex tw-justify-end tw-mb-4">
      <base-button @click="newDocument()" size="sm">
        <template v-slot:icon>
          <plus-icon /> </template
        >New Document
      </base-button>
    </div>
    <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-2">
      <template #icon="{item: document}">
        <component class="tw-h-6 tw-w-5" :is="getIcon(document.extension)"></component>
      </template>
      <template #document="{item: document}">
        <base-button type="text" @click="view(document)">{{ document.name }}</base-button>
        <base-badge v-if="document.isMerge == true" class="tw-ml-2" size="sm" color="gray">Merged</base-badge>
        <div class="tw-text-xs tw-text-gray-500">{{ getDocumentType(document.type) }}</div>
      </template>
      <template #created="{item: document}">
        <div>{{ formatDate(document.insertDate) }}</div>
        <div v-if="document.insertByUserName !== 'System'" class="tw-text-xs tw-text-gray-500 tw-flex">
          {{ document.insertByUserName }}
        </div>
      </template>
      <template #actions="{item: document}">
        <div class="tw-flex tw-items-center tw-space-x-2">
          <base-button type="flat" @click="view(document)" v-if="VIEWABLE_EXTENSIONS.includes(document.extension)">
            <template v-slot:icon>
              <eye-icon /> </template
            >View
          </base-button>
          <base-button type="flat" @click="download(document)" :loading="downloadingId === document.documentId">
            <template v-slot:icon>
              <download-icon /> </template
            >Download
          </base-button>
          <base-button v-if="getDocumentType(document.type) == 'invoice'" type="flat" @click="sendEmail(document)" :loading="sendingEmailId === document.documentId">
            <template v-slot:icon>
              <mail-icon /> </template
            >Send Email
          </base-button>
          <base-button type="flat" v-if="!isPscSecondaryRole" @click="del(document)">
            <template v-slot:icon>
              <trash-icon /> </template
            >Delete
          </base-button>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-12 tw-w-12 tw-rounded-full" /></td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex">
            <base-skeleton-loader class="tw-h-3 tw-w-40" />
            <base-skeleton-loader class="tw-h-3 tw-w-12 tw-rounded-full tw-ml-3" />
          </div>
          <base-skeleton-loader class="tw-h-2 tw-w-24 tw-mt-2" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <document-modal ref="documentModal" />
    <business-member-document-delete :businessId="businessId" :userId="userId" ref="deleteConfirmationModal" @delete="loadDocuments()" />
    <business-member-document-upload :businessId="businessId" :userId="userId" ref="newDocument" @save="loadDocuments()" />
  </div>
</template>

<script>
import DocumentModal from "@/components/DocumentModal";
import BusinessMemberDocumentDelete from "@/views/business/member/components/BusinessMemberDocumentDelete";
import BusinessMemberDocumentUpload from "@/views/business/member/components/BusinessMemberDocumentUpload";
import moment from "moment";
import { VIEWABLE_EXTENSIONS } from "@/constants/constants";
import { PlusIcon, DownloadIcon, TrashIcon, EyeIcon, PdfIcon, PngIcon, JpgIcon, MsWordIcon, MsExcelIcon, MsPowerpointIcon, MailIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";

const TAKE_KEY = "predictile_business_member_documents_take";

export default {
  name: "BusinessMemberDocuments",
  components: {
    DocumentModal,
    BusinessMemberDocumentDelete,
    BusinessMemberDocumentUpload,
    PlusIcon,
    DownloadIcon,
    TrashIcon,
    EyeIcon,
    PdfIcon,
    PngIcon,
    JpgIcon,
    MsWordIcon,
    MsExcelIcon,
    MsPowerpointIcon,
    MailIcon,
  },
  props: {
    businessId: String,
    userId: String,
  },
  data() {
    return {
      isLoading: false,
      downloadingId: null,
      sendingEmailId: null,
      items: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      fields: [
        { title: "", key: "icon", thClass: "tw-w-0" },
        { title: "Document", key: "document", thClass: "tw-flex" },
        { title: "Uploaded", key: "created" },
        { title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" },
      ],
      VIEWABLE_EXTENSIONS,
    };
  },
  computed: {
    ...mapGetters("role", ["isLocationFiltered", "isPredictile", "isBusinessManagement", "isBusinessAdministrator", "isPscSecondaryRole"]),
  },
  created() {
    this.loadDocuments();
  },
  methods: {
    getIcon(extension) {
      switch (extension) {
        case "png":
          return PngIcon;
        case "jpg":
        case "jpeg":
          return JpgIcon;
        case "pdf":
          return PdfIcon;
        case "docx":
        case "doc":
          return MsWordIcon;
        case "xlsx":
        case "xls":
          return MsExcelIcon;
        case "pptx":
        case "ppt":
          return MsPowerpointIcon;
        default:
          return null;
      }
    },
    getDocumentType(type) {
      switch (type) {
        case "rx_details":
          return "Rx Details";
        case "lifefile_order":
        case "rpc2b_order":
        case "rx_with_controlled_substance":
          return "Controlled Substance Rx";
      }

      return type;
    },
    loadDocuments() {
      this.isLoading = true;

      this.$axios
        .get(`/businesses/${this.businessId}/users/${this.userId}/documents?skip=${this.page.skip}&take=${this.page.take}`)
        .then((res) => {
          this.items = res.data.items;
          this.page.total = res.data.total;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load documents",
            message: "We had a problem retrieving the list of documents. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    del(item) {
      this.$refs.deleteConfirmationModal.show(item);
    },
    formatDate(date) {
      return new moment.utc(date).local().format("MMM D, YYYY");
    },
    download(item) {
      this.downloadingId = item.documentId;
      this.$axios
        .get(`/documents/${item.documentId}`)
        .then((res) => {
          this.$axios
            .get(res.data.downloadUrl, { responseType: "blob" })
            .then((response) => {
              var blob = new Blob([response.data]);
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = res.data.name + "." + res.data.extension;
              link.click();
              URL.revokeObjectURL(link.href);
            })
            .finally(() => {
              this.downloadingId = null;
            });
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to download document",
            message: "We had a problem retrieving the document documents. Please reload the page and try again.",
            error: err,
          });
          this.downloadingId = null;
        });
    },
    view(item) {
      this.$refs.documentModal.show(item);
    },
    newDocument() {
      this.$refs.newDocument.show();
    },
    async sendEmail(item) {
      this.sendingEmailId = item.documentId; // Show loading state

      try {
        // Send email request
        var response = await this.$axios.post(`/documents/${item.documentId}/send-email`);
        if (response.status === 200) {
          this.sendingEmailId = null;
          // Notify user of success
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "Email Sent",
            message: "The invoice has been sent successfully to the patient.",
          });
        }
      } catch (error) {
        console.error("Error sending email:", error); // Log for debugging

        let errorMessage = "There was a problem sending the email. Please try again.";

        // Check if error response exists and extract message
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Failed to Send Email",
          message: errorMessage,
        });
      } finally {
        this.sendingEmailId = null; // Reset loading state
      }
    },
  },
  watch: {
    userId() {
      this.loadDocuments();
    },
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
  },
};
</script>
